import {
  Box,
  Button,
  Image,
  Portal,
  Text,
  UseHoverCardProps,
  VStack
} from "@chakra-ui/react"
import { IconName } from "@pathwright/pathicons"
import useHoverCardOrPopover from "@pathwright/ux/src/components/chakra/useHoverCardOrPopover"
import Link from "@pathwright/ux/src/components/link/Link"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import HTMLText from "@pathwright/ux/src/components/text/HTMLText"
import { ReactNode } from "react"
import { PackageFragment } from "@pathwright/graphql-client/src/generated"
import {
  packageFocusVisibleOnlyStyles,
  usePackageContext
} from "./PackageProvider"
import { PackageVariant } from "./types"

import { getPackageParams, mergePackageParamsIntoURL } from "./utils"

type PackageButtonRenderer = (packageData?: PackageFragment) => ReactNode

export type PackageViewProps = {
  packageData: PackageFragment
  variant?: PackageVariant
  displayDiscount?: boolean
  renderButton?: PackageButtonRenderer
  primaryColor?: string
  backgroundColor?: string
  actionButtonLabel?: string
  actionButtonHref?: string
  actionButtonTarget?: "_self" | "_top" | "_blank"
}

export const getPackageName = (packageData: PackageFragment) =>
  packageData.display_name || packageData.name || "Package"

const getPackageDescription = (packageData: PackageFragment) =>
  packageData.public_description || ""

/** Adds target="_blank" (and rel when missing) to opening <a> tags that omit target. */
const defaultOpenPackageDescriptionLinksInNewTab = (html: string) => {
  if (!html) return html
  return html.replace(/<a\b([^<>]*)>/gi, (full, attrs: string) => {
    if (/\btarget\s*=/i.test(attrs)) return full
    const rel = /\brel\s*=/i.test(attrs) ? "" : ` rel="noopener noreferrer"`
    return `<a${attrs} target="_blank"${rel}>`
  })
}

export const PackageIdentity = ({ isTag }: { isTag?: boolean }) => {
  const { packageData, theme } = usePackageContext()

  if (!packageData) {
    return null
  }

  const packageIcon = (packageData.pathicon || "sparkles") as IconName
  const imageSize = isTag ? "1.1rem" : 10
  const pathiconSize = isTag ? "1.2rem" : "2rem"

  return (
    <>
      {packageData.image ? (
        <Image
          src={packageData.image}
          alt={`${getPackageName(packageData)} logo`}
          w={imageSize}
          h={imageSize}
          mb={!isTag ? 2 : undefined}
          borderRadius="5px"
          objectFit="cover"
          flexShrink={0}
        />
      ) : packageData.pathicon ? (
        <Pathicon
          icon={packageIcon}
          size={pathiconSize}
          color={theme.primaryColor}
          m={0}
          mb={!isTag ? 2 : undefined}
        />
      ) : null}
      <Text
        className="Package__name"
        lineClamp={1}
        fontWeight="semibold"
        // Add a little vertical spacing when there's not an image
        mx={isTag && !packageData.image ? "5px" : undefined}
        m={0}
        mb={isTag ? "-2px" : undefined}
        maxW="300px"
        fontSize={isTag ? "xs" : "lg"}
        lineHeight={isTag ? "1" : undefined}
        color={!isTag ? theme.textColor : undefined}
      >
        {getPackageName(packageData)}
      </Text>
    </>
  )
}

export const PackageDiscountBadge = ({
  packageData
}: {
  packageData: PackageFragment
}) => {
  const { t } = useTranslate()

  return (
    <VStack
      fontSize="sm"
      color="white"
      borderRadius="full"
      bgColor="gray.700"
      p={2}
      pos="absolute"
      transform="translate(50%, -60%)"
      top={0}
      right={0}
      gap={"2px"}
    >
      <Text asChild m={0} fontWeight="700" fontSize="xs" lineHeight="1">
        <span>{packageData.coupon_discount}%</span>
      </Text>
      <Text asChild m={0} fontSize="xs" lineHeight="1">
        <span>{t("package_ui.discount_off", "off")}</span>
      </Text>
    </VStack>
  )
}

const PackageActionButton = () => {
  const { t } = useTranslate()
  const { packageData, theme, actionButton } = usePackageContext()

  const { label, href, target, render, show } = actionButton || {}

  const urlParams = getPackageParams()
  const pricingURL = `/pricing/?${urlParams.toString()}`

  const { primaryColor } = theme

  const ctaButtonColorStyles = {
    backgroundColor: `${primaryColor} !important`,
    "&:hover, &[data-hover], &:active, &[data-active], &:focus-visible": {
      backgroundColor: `${primaryColor} !important`
    }
  }

  const defaultActivateNowLabel = t("package_ui.activate_now", "Activate Now")
  const resolvedButtonLabel = label?.trim() || defaultActivateNowLabel
  const resolvedButtonHref = href?.trim() || pricingURL
  const resolvedButtonTarget = target || "_self"
  const hasCustomActionButton = !!label || !!href
  const resolvedButtonHrefWithParams = hasCustomActionButton
    ? mergePackageParamsIntoURL(resolvedButtonHref, urlParams)
    : resolvedButtonHref

  if (show === false) return null

  return typeof render === "function" ? (
    <Box>{render(packageData || undefined)}</Box>
  ) : render !== false &&
    (packageData?.coupon_discount || packageData?.default_billing_plan_id) ? (
    <Button
      variant="solid"
      colorPalette="brand"
      asChild
      borderRadius="0"
      textDecoration="none"
      color="blackAlpha.800"
      css={{
        display: "flex",
        minHeight: "58px",
        borderRadius: "0",
        ...ctaButtonColorStyles
      }}
    >
      {hasCustomActionButton ? (
        <a
          href={resolvedButtonHrefWithParams}
          target={resolvedButtonTarget}
          rel={
            resolvedButtonTarget === "_blank"
              ? "noopener noreferrer"
              : undefined
          }
        >
          <Pathicon icon="rocket" />
          {resolvedButtonLabel}
        </a>
      ) : (
        <Link to={pricingURL} target={resolvedButtonTarget || "_blank"}>
          <Pathicon icon="rocket" />
          {defaultActivateNowLabel}
        </Link>
      )}
    </Button>
  ) : null
}

export const PackageDetails = () => {
  const { t } = useTranslate()
  const { packageData, theme, variant } = usePackageContext()

  const accentColor = theme.primaryColor
  const secondaryTextColor = theme.secondaryTextColor

  if (!packageData) {
    return null
  }

  return (
    <VStack
      className="PackageDetails"
      align="stretch"
      gap={2}
      px={0}
      pt={4}
      pb={0}
      pos="relative"
    >
      <VStack align="center" gap={1} py={0} px={4}>
        <PackageIdentity />
      </VStack>
      <Box
        px={4}
        css={{
          "& .HTMLText, & .HTMLText div, & .HTMLText p, & .HTMLText span": {
            color: secondaryTextColor
          },
          "& .HTMLText a, & .HTMLText a:visited": {
            color: `${accentColor} !important`
          },
          "& a": {
            textDecoration: "none"
          }
        }}
      >
        <HTMLText
          html={defaultOpenPackageDescriptionLinksInNewTab(
            getPackageDescription(packageData)
          )}
        />
      </Box>
      {packageData.coupon_discount ? (
        <Text px={4} textAlign="center" color={secondaryTextColor} mb="0.5rem">
          <Text
            asChild
            color="green.500"
            css={{
              "& svg": {
                // Dark mode causes the Pathicon to be white
                fill: "var(--chakra-colors-green-500)"
              }
            }}
            fontSize={{ base: "sm", md: "md" }}
          >
            <span>
              <Pathicon icon="check" color="var(--chakra-colors-green-500)" />{" "}
              {t("package_ui.save_percent", {
                percent: packageData.coupon_discount,
                defaultValue: `Save ${packageData.coupon_discount}%`
              })}
            </span>
          </Text>{" "}
          {t("package_ui.with_offer", "with offer")}
        </Text>
      ) : null}
      {variant === "card" && <PackageActionButton />}
    </VStack>
  )
}

export const PackagePopover = ({
  renderTrigger,
  positioning
}: {
  renderTrigger: (Trigger: React.ElementType) => ReactNode
  positioning?: UseHoverCardProps["positioning"]
}) => {
  const { packageData, theme } = usePackageContext()
  const HoverCardOrPopover = useHoverCardOrPopover()

  if (!packageData) {
    return null
  }

  const backgroundColor = theme.backgroundColor
  const textColor = theme.textColor

  return (
    <HoverCardOrPopover.Root
      positioning={positioning}
      closeOnEscape
      closeOnInteractOutside
      lazyMount
      unmountOnExit={false}
      autoFocus={false}
    >
      {renderTrigger(HoverCardOrPopover.Trigger)}
      <Portal>
        <HoverCardOrPopover.Positioner>
          <HoverCardOrPopover.Content
            maxW="sm"
            p={0}
            borderRadius="var(--chakra-radii-card)"
            boxShadow="0 1px 4px #0000001a,0 0 0 1px #0000001a,0 4px 10px #0000001a,inset 0 0 0 1px #ffffff1a"
            css={{
              ...packageFocusVisibleOnlyStyles,
              "--arrow-background": backgroundColor,
              "--hovercard-bg": backgroundColor,
              "& .chakra-popover__arrowTip": {
                bgColor: `${backgroundColor} !important`
              }
            }}
          >
            <HoverCardOrPopover.Arrow>
              <HoverCardOrPopover.ArrowTip borderColor={backgroundColor} />
            </HoverCardOrPopover.Arrow>
            <Box
              borderRadius="var(--chakra-radii-card)"
              overflow="hidden"
              bg={backgroundColor}
              color={textColor}
              p={0}
              border="none"
            >
              <PackageDetails />
            </Box>
          </HoverCardOrPopover.Content>
        </HoverCardOrPopover.Positioner>
      </Portal>
    </HoverCardOrPopover.Root>
  )
}
